import { SearchMd } from "@untitled-ui/icons-react";
import { compact } from "lodash";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArtifactType,
  CreatedArtifactFragmentFragment,
  MeetingDialogFragmentFragment,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import {
  getUrl,
  toWithBackground,
  toggleSidebarShortcutLabel,
} from "@helpers/helpers";

const LayoutAddSearch = ({
  onShowSearchModal,
  onHideSidebar,
  menuIsCollapsed,
  hideSearch = false,
}: {
  menuIsCollapsed: boolean;
  onShowSearchModal: () => void;
  onHideSidebar?: () => void;
  hideSearch?: boolean;
}) => {
  const location = useLocation<TFLocationState>();
  const currentOrganization = currentOrganizationVar();
  const [showCreationMeetingDialog, setShowCreationMeetingDialog] =
    useState(false);
  const [showCreationDialogArtifactType, setShowCreationDialogArtifactType] =
    useState<null | ArtifactType>(null);

  const link = useLink();
  const handleCloseCreationDialog = (
    artifact?: CreatedArtifactFragmentFragment,
  ) => {
    setShowCreationDialogArtifactType(null);
    if (artifact) {
      link.redirect(
        toWithBackground({
          pathname: getUrl({
            artifactId: artifact.id,
            artifactType: artifact.artifactType,
          }),
          location,
        }),
      );
    }
  };

  const label = useLabel();
  const options = compact([
    currentOrganization.featureFlags.askAi && {
      type: "link",
      label: "Start Ask AI chat",
      to: "/ask-ai",
    },
    currentOrganization.featureFlags.askAi && { type: "divider" },
    {
      type: "header",
      label: "Create an item",
    },
    currentOrganization.featureFlags.meetings && {
      label: "Meeting",
      onClick: () => setShowCreationMeetingDialog(true),
    },
    currentOrganization.featureFlags.actionItems && {
      label: label("action item", { titleCase: true }),
      onClick: () => setShowCreationDialogArtifactType(ArtifactType.ActionItem),
    },
    currentOrganization.featureFlags.goals && {
      label: label("goal", { titleCase: true }),
      onClick: () => setShowCreationDialogArtifactType(ArtifactType.Goal),
    },
    currentOrganization.featureFlags.recognitions && {
      label: label("recognition", { titleCase: true }),
      onClick: () =>
        setShowCreationDialogArtifactType(ArtifactType.Recognition),
    },
    currentOrganization.featureFlags.feedbacks && {
      label: label("feedback", { titleCase: true }),
      onClick: () => setShowCreationDialogArtifactType(ArtifactType.Feedback),
    },
  ]) as DropdownOptionType[];

  return (
    <div
      className={classNames(
        "flex items-center gap-0.5",
        menuIsCollapsed && "hidden",
      )}
    >
      {onHideSidebar && (
        <Button
          icon={getTopicflowIcon("chevronLeftDouble")}
          theme={buttonTheme.iconGray}
          size={ButtonSize.medium}
          onClick={onHideSidebar}
          aria-label="Hide sidebar button"
          tooltip={`Hide sidebar: ${toggleSidebarShortcutLabel()}`}
        />
      )}
      {currentOrganization.featureFlags.explorer && !hideSearch && (
        <Button
          icon={SearchMd}
          theme={buttonTheme.iconGray}
          size={ButtonSize.medium}
          onClick={onShowSearchModal}
          aria-label="Sidebar search button"
          tooltip="Search"
        />
      )}
      {options.length > 1 && (
        <Dropdown options={options}>
          <Button
            icon={getTopicflowIcon("plus")}
            theme={buttonTheme.iconGray}
            size={ButtonSize.medium}
            aria-label="Sidebar create artifact button"
            tooltip="Create item"
          />
        </Dropdown>
      )}
      {showCreationDialogArtifactType && (
        <ArtifactCreationDialog
          hideTypeSelector
          formOptions={{
            artifactType: showCreationDialogArtifactType,
          }}
          onClose={handleCloseCreationDialog}
        />
      )}

      {showCreationMeetingDialog && (
        <MeetingDialog
          onSaved={({
            meeting,
          }: {
            meeting: MeetingDialogFragmentFragment;
          }) => {
            setShowCreationMeetingDialog(false);
            link.redirect(
              `/meeting/${meeting?.meetingGroup?.id}/${meeting.id}`,
            );
          }}
          onClose={() => setShowCreationMeetingDialog(false)}
        />
      )}
    </div>
  );
};

export default LayoutAddSearch;
